import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import NotificationModal from "../components/NotificationModal";
import { uploadToStorage, compressImageToWebP } from "../utils/uploadToStorage";
import ImageCropperModal from "../components/ImageCropperModal";
import { Camera, Check, ChevronLeft } from "lucide-react";
import {
  validateCPF,
  validatePhone,
  validateCEP,
  validateDate,
  validateMinAge,
  validateName,
} from "../utils/validators";
// CadastroFamilia.css migrado para Tailwind

// =========================================================================
// FUNÇÕES DE UTILIDADE (MÁSCARAS)
// =========================================================================
const maskCPF = (value) => {
  return value
    .replace(/\D/g, "")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})/, "$1-$2")
    .replace(/(-\d{2})\d+?$/, "$1");
};

const maskCEP = (value) => {
  return value
    .replace(/\D/g, "")
    .replace(/(\d{5})(\d)/, "$1-$2")
    .replace(/(-\d{3})\d+?$/, "$1");
};

const maskPhone = (value) => {
  return value
    .replace(/\D/g, "")
    .replace(/(\d{2})(\d)/, "($1) $2")
    .replace(/(\d{5})(\d)/, "$1-$2")
    .replace(/(-\d{4})\d+?$/, "$1");
};

const diasSemana = [
  { key: "domingo", label: "Dom" },
  { key: "segunda", label: "Seg" },
  { key: "terca", label: "Ter" },
  { key: "quarta", label: "Qua" },
  { key: "quinta", label: "Qui" },
  { key: "sexta", label: "Sex" },
  { key: "sabado", label: "Sáb" },
];

const periodosTrabalho = [
  { key: "manha", label: "Manhã" },
  { key: "tarde", label: "Tarde" },
  { key: "noite", label: "Noite" },
  { key: "pernoite", label: "Pernoite" },
];

export default function CadastroFamilia({
  adminEditingUserId,
  hideHeader = false,
}) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isCepLoading, setIsCepLoading] = useState(false);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [tempImgSrc, setTempImgSrc] = useState(null);
  const [stepError, setStepError] = useState("");
  const [stepWarn, setStepWarn] = useState("");
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "info",
  });

  const [dados, setDados] = useState({
    nome: "",
    sobrenome: "",
    dataNascimento: "",
    cpf: "",
    cep: "",
    cidade: "",
    endereco: "",
    bairro: "",
    numero: "",
    complemento: "",
    estado: "",
    telefone: "",
    numCriancas: 1,
    faixaEtaria: [],
    necessidadesEspeciais: false,
    descricaoNecessidades: "",
    caracteristicasCrianca: "",
    frequenciaServico: {}, // Agora inicializado como objeto para a tabela (JSONB)
    descricaoFamilia: "", // bio
    banco: "",
    agencia: "",
    conta: "",
    pix: "",
    privacidade: "publico",
    fotoFile: null,
    fotoPreviewUrl: null,
    aceitouTermos: true,
  });

  const faixaEtariaOpcoes = [
    "0-2 anos (Bebês)",
    "3-5 anos (Pré-escolar)",
    "6-10 anos (Escolar)",
    "11+ anos (Pré-adolescente)",
  ];

  const showNotification = (message, type = "info") => {
    setNotification({ show: true, message, type });
  };

  const fetchExistingData = useCallback(async (userId) => {
    setLoading(true);
    try {
      const { data: userData, error: userError } = await supabase
        .from("usuarios")
        .select("*")
        .eq("id", userId)
        .single();

      if (userError && userError.code !== "PGRST116") {
        console.error("Erro ao buscar dados do usuário:", userError);
        throw userError;
      }

      const { data: clienteData, error: clienteError } = await supabase
        .from("usuarios_clientes")
        .select("*")
        .eq("usuario_id", userId)
        .single();

      if (clienteError && clienteError.code !== "PGRST116") {
        console.error("Erro ao buscar dados do cliente:", clienteError);
        throw clienteError;
      }

      if (userData || clienteData) {
        const combinedData = { ...userData, ...clienteData };

        setDados((prev) => ({
          ...prev,
          nome: combinedData.nome === "Novo" ? "" : combinedData.nome || "",
          sobrenome:
            combinedData.sobrenome === "Usuário"
              ? ""
              : combinedData.sobrenome || "",
          dataNascimento: combinedData.data_nascimento || "",
          cpf: maskCPF(combinedData.cpf || ""),
          cep: maskCEP(combinedData.cep || ""),
          cidade: combinedData.cidade || "",
          endereco: combinedData.endereco || "",
          bairro: combinedData.bairro || "",
          numero: combinedData.numero || "",
          complemento: combinedData.complemento || "",
          estado: combinedData.estado || "",
          telefone: maskPhone(combinedData.telefone || ""),
          numCriancas: combinedData.num_criancas || 1,
          faixaEtaria: combinedData.faixa_etaria_preferida || [],
          necessidadesEspeciais: combinedData.necessidades_especiais || false,
          descricaoNecessidades: combinedData.descricao_necessidades || "",
          caracteristicasCrianca: combinedData.caracteristicas_crianca || "",
          frequenciaServico: combinedData.frequencia_servico || {},
          descricaoFamilia: combinedData.bio || "",
          fotoPreviewUrl: combinedData.foto_url || null,
          aceitouTermos: combinedData.aceitou_termo || false,
        }));
      }
    } catch (err) {
      console.error("Erro ao carregar dados existentes:", err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const br = "\n";

  const steps = [
    {
      field: "nome",
      pergunta: `Oi! 😊 Vamos começar?${br}Qual é o seu nome?`,
      type: "text",
      placeholder: "Seu primeiro nome",
    },
    {
      field: "sobrenome",
      pergunta: `Muito prazer, ${dados.nome}! E o seu sobrenome?`,
      type: "text",
      placeholder: "Seu sobrenome completo",
    },
    {
      field: "cpf",
      pergunta: "Agora seu CPF (é seguro 🔒)",
      type: "text",
      placeholder: "Apenas números",
    },
    {
      field: "frequenciaServico",
      pergunta:
        "Em qual periodo você costuma precisar de babá? Selecione os dias e períodos que costuma precisar de apoio.",
      type: "disponibilidade",
    },
    {
      field: "dataNascimento",
      pergunta: "Qual sua data de nascimento?",
      type: "date",
    },
    {
      field: "cep",
      pergunta: "Qual seu CEP?",
      type: "text",
      placeholder: "00000-000",
    },
    {
      field: "cidade",
      pergunta: "Em qual cidade você mora?",
      type: "text",
      placeholder: "Sua cidade",
    },
    {
      field: "numero",
      pergunta: "Qual o número da sua residência?",
      type: "text",
      placeholder: "Número ou S/N",
    },
    {
      field: "complemento",
      pergunta: "Algum complemento? (Apto, Bloco, etc.)\n\n(Opcional)",
      type: "text",
      placeholder: "Ex: Apto 101",
    },
    {
      field: "telefone",
      pergunta: "Seu telefone com DDD?",
      type: "tel",
      placeholder: "(00) 00000-0000",
    },
    {
      field: "numCriancas",
      pergunta: "Quantas crianças precisam de cuidado na sua casa?",
      type: "number",
      placeholder: "Ex: 1",
    },
    {
      field: "faixaEtaria",
      pergunta: "Qual a faixa etária delas?",
      type: "multi-select",
      options: faixaEtariaOpcoes,
    },
    {
      field: "necessidadesEspeciais",
      pergunta: "Alguma das crianças possui necessidades especiais?",
      type: "boolean",
    },
    {
      field: "descricaoNecessidades",
      pergunta: "Poderia nos contar um pouco sobre essas necessidades?",
      type: "textarea",
      placeholder: "Detalhes...",
      condition: () => dados.necessidadesEspeciais,
    },
    {
      field: "caracteristicasCrianca",
      pergunta:
        "Como são seus filhos? Conte sobre a personalidade e a rotina deles.",
      type: "textarea",
      placeholder: "Elas gostam de brincar de...",
    },
    {
      field: "descricaoFamilia",
      pergunta: "Fale um pouco sobre a sua família 😊",
      type: "textarea",
      placeholder: "Nossa família é...",
    },
    {
      field: "banco",
      pergunta: "Para facilitar pagamentos, qual seu Banco?",
      type: "text",
      placeholder: "Ex: Nubank, Itaú...",
      optional: true,
    },
    {
      field: "agencia",
      pergunta: "Sua agência?",
      type: "text",
      placeholder: "Ex: 0001",
      optional: true,
    },
    {
      field: "conta",
      pergunta: "Sua conta?",
      type: "text",
      placeholder: "Ex: 12345-6",
      optional: true,
    },
    {
      field: "pix",
      pergunta: "Sua chave PIX?",
      type: "text",
      placeholder: "CPF, Celular, E-mail...",
      optional: true,
    },
    {
      field: "foto",
      pergunta: "Agora envie uma foto da família 😊",
      type: "file",
    },
    {
      field: "final",
      pergunta: "Tudo pronto! Vamos finalizar seu cadastro?",
      type: "final",
    },
  ];

  const currentStep = steps[step];

  const toggleDisponibilidade = (dia, periodo, field = "frequenciaServico") => {
    setDados((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [dia]: {
          ...(prev[field][dia] || {}),
          [periodo]: !prev[field][dia]?.[periodo],
        },
      },
    }));
  };

  const toggleDiaTodo = (dia, field = "frequenciaServico") => {
    setDados((prev) => {
      const fieldData = prev[field] || {};
      const estadoAtualDia = fieldData[dia] || {};
      const todosAtivos = periodosTrabalho.every((p) => estadoAtualDia[p.key]);
      const novoEstadoDia = {};
      periodosTrabalho.forEach((p) => {
        novoEstadoDia[p.key] = !todosAtivos;
      });
      return {
        ...prev,
        [field]: { ...fieldData, [dia]: novoEstadoDia },
      };
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if (name === "cpf") {
      newValue = maskCPF(value);
    } else if (name === "cep") {
      newValue = maskCEP(value);
    } else if (name === "telefone") {
      newValue = maskPhone(value);
    } else if (currentStep.type === "number") {
      newValue = value === "" ? "" : Number(value);
    }

    setStepError("");
    setStepWarn("");
    setDados((prev) => ({ ...prev, [name]: newValue }));
  };

  // 🔹 BUSCA AUTOMÁTICA DE CEP (apenas Rio de Janeiro)
  useEffect(() => {
    const fetchCEP = async () => {
      const cleanedCep = dados.cep.replace(/\D/g, "");
      if (cleanedCep.length === 8) {
        setIsCepLoading(true);
        try {
          const response = await fetch(
            `https://viacep.com.br/ws/${cleanedCep}/json/`,
          );
          const data = await response.json();

          if (data.erro) {
            setStepError("CEP não encontrado. Verifique e tente novamente.");
          } else if (data.uf !== "RJ") {
            setStepError(
              `Este CEP pertence a ${data.localidade || "outro estado"} (${data.uf || "UF desconhecida"}). O serviço Tesourinhos atende apenas o Rio de Janeiro.`,
            );
            setDados((prev) => ({
              ...prev,
              cep: "",
              endereco: "",
              bairro: "",
              cidade: "",
              estado: "",
            }));
          } else {
            setStepError("");
            setDados((prev) => ({
              ...prev,
              endereco: data.logradouro || "",
              bairro: data.bairro || "",
              cidade: data.localidade || "",
              estado: data.uf || "",
            }));
          }
        } catch (err) {
          console.error("Erro ao buscar CEP:", err);
        } finally {
          setIsCepLoading(false);
        }
      }
    };
    fetchCEP();
  }, [dados.cep]);

  const toggleTag = (tag, field) => {
    setDados((prev) => {
      const currentList = prev[field] || [];
      const updatedList = currentList.includes(tag)
        ? currentList.filter((item) => item !== tag)
        : [...currentList, tag];
      return {
        ...prev,
        [field]: updatedList,
      };
    });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const processedFile = await compressImageToWebP(file, {
        maxDimension: 2000,
        maxSizeBytes: 5 * 1024 * 1024,
      });
      const url = URL.createObjectURL(processedFile);
      setTempImgSrc(url);
      setCropperOpen(true);
      e.target.value = null;
    } catch (err) {
      console.error("Erro ao processar imagem:", err);
      showNotification(
        "Não foi possível processar o arquivo. Tente outra imagem.",
        "error",
      );
      e.target.value = null;
    }
  };

  const handleCropComplete = async (croppedBlob) => {
    try {
      const finalFile = await compressImageToWebP(croppedBlob, {
        maxDimension: 800,
        maxSizeBytes: 150 * 1024,
      });

      if (dados.fotoPreviewUrl) {
        URL.revokeObjectURL(dados.fotoPreviewUrl);
      }

      setDados((prev) => ({
        ...prev,
        fotoFile: finalFile,
        fotoPreviewUrl: URL.createObjectURL(finalFile),
      }));
      setCropperOpen(false);
      setTempImgSrc(null);
    } catch (err) {
      console.error("Erro ao processar corte:", err);
      showNotification("Erro ao processar a imagem cortada.", "error");
    }
  };

  const handleNext = async () => {
    setStepError("");
    setStepWarn("");

    // ── Campos obrigatórios simples ──────────────────────────────────────────
    if (
      currentStep.field !== "final" &&
      currentStep.type !== "file" &&
      !currentStep.optional &&
      !["multi-select", "boolean", "select", "disponibilidade"].includes(currentStep.type) &&
      currentStep.field !== "complemento" &&
      !dados[currentStep.field]
    ) {
      setStepError("Preencha este campo antes de continuar.");
      return;
    }

    // ── Nome ─────────────────────────────────────────────────────────────────
    if (currentStep.field === "nome" && dados.nome) {
      if (!validateName(dados.nome)) {
        setStepError("Nome inválido. Use apenas letras e espaços.");
        return;
      }
    }
    if (currentStep.field === "sobrenome" && dados.sobrenome) {
      if (!validateName(dados.sobrenome)) {
        setStepError("Sobrenome inválido. Use apenas letras e espaços.");
        return;
      }
    }

    // ── CPF ──────────────────────────────────────────────────────────────────
    if (currentStep.field === "cpf") {
      if (!validateCPF(dados.cpf)) {
        setStepError("CPF inválido. Verifique os números informados.");
        return;
      }
      setLoading(true);
      try {
        const cleanCPF = dados.cpf.replace(/\D/g, "");
        const { data: exists } = await supabase.rpc("rpc_check_cpf_exists", {
          p_cpf: cleanCPF,
        });
        if (exists) {
          setNotification({
            show: true,
            message:
              "Já existe uma conta vinculada a este CPF. Vamos te redirecionar para o login.",
            type: "info",
            onAfterClose: () => navigate("/login"),
          });
          setLoading(false);
          return;
        }
      } catch (err) {
        console.error("Erro ao verificar CPF:", err);
      } finally {
        setLoading(false);
      }
    }

    // ── Data de Nascimento ───────────────────────────────────────────────────
    if (currentStep.field === "dataNascimento" && dados.dataNascimento) {
      if (!validateDate(dados.dataNascimento)) {
        setStepError("Data inválida. Informe uma data real posterior a 1900.");
        return;
      }
      if (!validateMinAge(dados.dataNascimento, 18, 100)) {
        setStepError("Você precisa ter entre 18 e 100 anos para se cadastrar.");
        return;
      }
    }

    // ── Telefone ─────────────────────────────────────────────────────────────
    if (currentStep.field === "telefone" && dados.telefone) {
      const phoneResult = validatePhone(dados.telefone);
      if (!phoneResult.valid) {
        setStepError(phoneResult.msg);
        return;
      }
      if (phoneResult.warn) {
        setStepWarn(phoneResult.warn);
      }
    }

    // ── CEP ──────────────────────────────────────────────────────────────────
    if (currentStep.field === "cep" && dados.cep) {
      if (!validateCEP(dados.cep)) {
        setStepError("CEP incompleto. Informe todos os 8 dígitos.");
        return;
      }
    }

    // ── Multi-select obrigatório ──────────────────────────────────────────────
    if (
      currentStep.type === "multi-select" &&
      dados[currentStep.field].length === 0
    ) {
      setStepError("Selecione pelo menos uma opção.");
      return;
    }

    // ── Disponibilidade ───────────────────────────────────────────────────────
    if (currentStep.type === "disponibilidade") {
      const fieldData = dados[currentStep.field] || {};
      const temAlgum = Object.values(fieldData).some((dia) =>
        Object.values(dia).some((valor) => valor === true),
      );
      if (!temAlgum) {
        setStepError("Selecione pelo menos um dia e período.");
        return;
      }
    }

    // ── Foto obrigatória ──────────────────────────────────────────────────────
    if (currentStep.type === "file" && !dados.fotoFile) {
      setStepError("Por favor, selecione uma foto para continuar.");
      return;
    }

    if (currentStep.field === "final") {
      handleSubmit();
      return;
    }

    let nextStep = step + 1;
    while (
      nextStep < steps.length &&
      steps[nextStep].condition &&
      !steps[nextStep].condition()
    ) {
      nextStep++;
    }
    setStep(nextStep);
  };

  const handleBack = () => {
    let prevStep = step - 1;
    while (
      prevStep >= 0 &&
      steps[prevStep].condition &&
      !steps[prevStep].condition()
    ) {
      prevStep--;
    }
    setStep(prevStep);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      let targetId = adminEditingUserId;
      let authUser = null;
      if (!targetId) {
        const {
          data: { session: currentSession },
        } = await supabase.auth.getSession();
        authUser = currentSession?.user ?? null;

        if (!authUser) {
          const {
            data: { user: serverUser },
          } = await supabase.auth.getUser();
          authUser = serverUser ?? null;
        }

        if (!authUser) {
          showNotification(
            "Sessão expirada. Você será redirecionada para o login.",
            "error",
          );
          setTimeout(() => navigate("/login"), 2500);
          return;
        }

        targetId = authUser.id;
      }

      let fotoPath = null;
      if (dados.fotoFile) {
        console.log(
          "DEBUG [Família]: Iniciando upload da imagem...",
          dados.fotoFile,
        );
        const { path, error: uploadError } = await uploadToStorage(
          dados.fotoFile,
          "familia",
          { userId: targetId },
        );
        if (uploadError) throw new Error(uploadError);
        fotoPath = path;
        console.log("DEBUG [Família]: Upload concluído. Path:", fotoPath);
      }

      const rpcPayload = {
        p_user_id: targetId,
        p_basic_data: {
          nome: dados.nome,
          sobrenome: dados.sobrenome,
          cidade: dados.cidade,
          estado: dados.estado,
          cep: dados.cep.replace(/\D/g, ""),
          endereco: dados.endereco,
          bairro: dados.bairro,
          numero: dados.numero,
          complemento: dados.complemento,
          telefone: dados.telefone.replace(/\D/g, ""),
          bio: dados.descricaoFamilia,
          banco: dados.banco,
          agencia: dados.agencia,
          conta: dados.conta,
          pix: dados.pix,
          foto_url: fotoPath,
          aceitou_termo: true,
        },
        p_extra_data: {
          num_criancas: dados.numCriancas,
          faixa_etaria_preferida: dados.faixaEtaria,
          caracteristicas_crianca: dados.caracteristicasCrianca,
          necessidades_especiais: dados.necessidadesEspeciais,
          descricao_necessidades: dados.descricaoNecessidades,
          horarios_preferidos: dados.frequenciaServico,
          frequencia_servico: dados.frequenciaServico, // Agora enviado como objeto JSONB
          cpf: dados.cpf.replace(/\D/g, ""),
          data_nascimento: dados.dataNascimento || null,
        },
        p_role: "cliente",
      };

      console.log(
        "DEBUG [Família]: Enviando dados para RPC (rpc_save_user_profile):",
        rpcPayload,
      );

      const { error } = await supabase.rpc("rpc_save_user_profile", rpcPayload);

      if (error) throw error;

      // Cadastro concluído → vai para análise do admin (só promove de 'incompleto').
      if (!adminEditingUserId) {
        try {
          await supabase.rpc("rpc_mark_docs_submitted", {
            p_user_id: rpcPayload.p_user_id,
          });
        } catch (markError) {
          console.warn("Falha ao promover doc_status para 'enviado':", markError);
        }
      }

      showNotification("Cadastro finalizado com sucesso!", "success");
      // Only navigate if not admin editing
      if (!adminEditingUserId) {
        setTimeout(() => navigate("/escolher-assinatura"), 2000);
      }
    } catch (err) {
      showNotification("Erro ao salvar cadastro: " + err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-[var(--color-warm-200)] bg-white px-4 py-3 text-sm text-[var(--color-warm-800)] placeholder:text-[var(--color-warm-400)] focus:outline-none focus:ring-2 focus:ring-[var(--color-rose-400)/40] focus:border-[var(--color-rose-400)] transition";
  const tagCls = (active) => `rounded-xl border px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${active ? "border-[var(--color-rose-400)] bg-[var(--color-rose-400)/10] text-[var(--color-rose-400)]" : "border-[var(--color-warm-200)] bg-white text-[var(--color-warm-700)] hover:border-[var(--color-rose-400)]"}`;
  const circleCls = (ativo) => `flex h-7 w-7 items-center justify-center rounded-full border transition cursor-pointer ${ativo ? "border-[var(--color-rose-400)] bg-[var(--color-rose-400)] text-white" : "border-[var(--color-warm-200)] bg-white text-transparent hover:border-[var(--color-rose-400)]"}`;

  const renderInput = () => {
    const errorBlock = stepError ? (
      <p className="mt-2 text-xs font-medium text-red-500">{stepError}</p>
    ) : stepWarn ? (
      <p className="mt-2 text-xs font-medium text-amber-500">{stepWarn}</p>
    ) : null;

    switch (currentStep.type) {
      case "text":
      case "tel":
      case "date":
      case "number":
        return (
          <>
            <input
              key={currentStep.field}
              id="family-reg-field-input"
              type={currentStep.type === "tel" ? "text" : currentStep.type}
              name={currentStep.field}
              value={dados[currentStep.field] || ""}
              onChange={handleChange}
              placeholder={currentStep.placeholder}
              className={`${inputCls}${stepError ? " border-red-400 ring-1 ring-red-400/40" : ""}`}
              autoFocus
              onKeyDown={(e) => e.key === "Enter" && handleNext()}
            />
            {errorBlock}
          </>
        );
      case "textarea":
        return (
          <>
            <textarea
              key={currentStep.field}
              id="family-reg-field-textarea"
              name={currentStep.field}
              value={dados[currentStep.field] || ""}
              onChange={handleChange}
              placeholder={currentStep.placeholder}
              rows={4}
              className={inputCls + " resize-none"}
              autoFocus
            />
            {errorBlock}
          </>
        );
      case "boolean":
        return (
          <div id="family-reg-bool-options" className="flex flex-wrap gap-3">
            {[{ label: "Sim", value: true }, { label: "Não", value: false }].map(({ label, value }) => (
              <button
                key={label}
                id={`family-reg-bool-${label === "Sim" ? "yes" : "no"}`}
                type="button"
                className={tagCls(dados[currentStep.field] === value)}
                onClick={() => setDados({ ...dados, [currentStep.field]: value })}
                disabled={loading}
              >
                {label}
              </button>
            ))}
          </div>
        );
      case "multi-select":
        return (
          <div id="family-reg-tags-wrapper" className="flex flex-wrap gap-2">
            {currentStep.options.map((opt) => (
              <button
                key={opt}
                id={`family-reg-tag-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                type="button"
                className={tagCls(dados[currentStep.field].includes(opt))}
                onClick={() => toggleTag(opt, currentStep.field)}
                disabled={loading}
              >
                {opt}
              </button>
            ))}
          </div>
        );
      case "disponibilidade": {
        const fieldName = currentStep.field;
        return (
          <div id="family-reg-disp-table-container" className="overflow-x-auto -mx-2">
            <table className="w-full min-w-[320px] text-xs">
              <thead>
                <tr>
                  <th className="w-10" />
                  {periodosTrabalho.map((p) => (
                    <th key={p.key} className="py-1 px-1 text-center font-medium text-[var(--color-warm-500)]">{p.label}</th>
                  ))}
                  <th className="py-1 px-1 text-center font-medium text-[var(--color-warm-500)]">Todos</th>
                </tr>
              </thead>
              <tbody>
                {diasSemana.map((dia) => (
                  <tr key={dia.key}>
                    <td className="py-1 pr-2 font-medium text-[var(--color-warm-700)]">{dia.label}</td>
                    {periodosTrabalho.map((periodo) => {
                      const ativo = dados[fieldName][dia.key]?.[periodo.key];
                      return (
                        <td key={periodo.key} className="py-1 px-1 text-center">
                          <div
                            id={`family-reg-disp-${dia.key}-${periodo.key}`}
                            className={circleCls(ativo) + " mx-auto"}
                            onClick={() => toggleDisponibilidade(dia.key, periodo.key, fieldName)}
                          >
                            {ativo && <Check size={12} strokeWidth={3} />}
                          </div>
                        </td>
                      );
                    })}
                    <td className="py-1 px-1 text-center">
                      {(() => {
                        const todosAtivos = periodosTrabalho.every((p) => dados[fieldName][dia.key]?.[p.key]);
                        return (
                          <div
                            id={`family-reg-disp-${dia.key}-all`}
                            className={circleCls(todosAtivos) + " mx-auto"}
                            onClick={() => toggleDiaTodo(dia.key, fieldName)}
                          >
                            {todosAtivos && <Check size={12} strokeWidth={3} />}
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
      case "file":
        return (
          <label id="family-reg-photo-upload-wrapper" htmlFor="foto-input-fam" className="block cursor-pointer">
            <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-[var(--color-warm-200)] bg-[var(--color-warm-50)] py-8 gap-3 hover:border-[var(--color-rose-400)] transition-colors">
              {dados.fotoPreviewUrl ? (
                <img
                  id="family-reg-photo-preview"
                  src={dados.fotoPreviewUrl}
                  alt="Preview"
                  className="h-28 w-28 rounded-full object-cover ring-4 ring-[var(--color-rose-400)/30]"
                />
              ) : (
                <>
                  <Camera size={40} className="text-[var(--color-warm-400)]" />
                  <span className="text-sm text-[var(--color-warm-500)]">Escolher foto da família</span>
                </>
              )}
            </div>
            <input id="foto-input-fam" type="file" accept="image/*" onChange={handleFileChange} hidden />
          </label>
        );
      case "final":
        return (
          <p id="family-reg-final-step-msg" className="text-center text-sm text-[var(--color-warm-600)]">
            Clique no botão abaixo para confirmar seus dados e começar a encontrar a babá ideal!
          </p>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (adminEditingUserId) {
      fetchExistingData(adminEditingUserId);
    } else {
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) fetchExistingData(session.user.id);
      });
    }
  }, [adminEditingUserId, fetchExistingData]);

  return (
    <div id="family-reg-chat-wrapper" className="min-h-screen bg-gradient-to-br from-[var(--color-warm-50)] via-white to-[var(--color-warm-100)] flex flex-col">
      {!hideHeader && (
        <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-[var(--color-warm-100)] px-4 py-3 flex items-center gap-3">
          <img src="/assets/logo-tesourinhos-trans.png" alt="Tesourinhos" className="h-8 w-auto" />
          <span className="text-sm font-semibold text-[var(--color-warm-700)]">Cadastro de Família</span>
        </header>
      )}

      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-lg">
          {/* Progress */}
          <div className="flex items-center gap-3 mb-6">
            <div className="flex-1 h-2 rounded-full bg-[var(--color-warm-200)] overflow-hidden">
              <div
                className="h-2 rounded-full bg-gradient-to-r from-[var(--color-rose-400)] to-[var(--color-slate-blue-400)] transition-all duration-500"
                style={{ width: `${((step + 1) / steps.length) * 100}%` }}
              />
            </div>
            <span className="text-xs font-medium text-[var(--color-warm-500)] shrink-0">{step + 1}/{steps.length}</span>
          </div>

          {/* Card */}
          <div id="family-reg-chat-card" className="bg-white rounded-3xl shadow-lg px-6 py-7 sm:px-8">
            <p id="family-reg-chat-question" className="font-heading text-xl font-bold text-[var(--color-warm-900)] mb-6 whitespace-pre-line leading-snug">
              {isCepLoading ? "Buscando seu endereço... 🔍" : currentStep.pergunta}
            </p>
            <div>{renderInput()}</div>
          </div>

          {/* Navigation */}
          <div className="flex gap-3 mt-5">
            {step > 0 && (
              <button
                id="family-reg-btn-back"
                type="button"
                onClick={handleBack}
                disabled={loading}
                className="flex items-center gap-1 rounded-2xl border border-[var(--color-warm-300)] bg-white px-5 py-3 text-sm font-semibold text-[var(--color-warm-700)] hover:bg-[var(--color-warm-50)] transition-colors disabled:opacity-50"
              >
                <ChevronLeft size={16} /> Voltar
              </button>
            )}
            <button
              id="family-reg-btn-next"
              type="button"
              onClick={handleNext}
              disabled={loading}
              className="flex-1 rounded-2xl bg-[var(--color-rose-400)] px-6 py-3 text-sm font-bold text-white shadow-md hover:brightness-105 transition-all disabled:opacity-60"
            >
              {loading ? "Salvando..." : step === steps.length - 1 ? "Finalizar Cadastro" : "Continuar"}
            </button>
          </div>
        </div>
      </div>

      <NotificationModal
        show={notification.show}
        message={notification.message}
        type={notification.type}
        onClose={() => {
          const callback = notification.onAfterClose;
          setNotification({ ...notification, show: false, onAfterClose: null });
          if (callback) callback();
        }}
      />

      {cropperOpen && tempImgSrc && (
        <ImageCropperModal
          imageSrc={tempImgSrc}
          aspect={1}
          onCropComplete={handleCropComplete}
          onClose={() => setCropperOpen(false)}
        />
      )}
    </div>
  );
}
